import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AdminLayout from './AdminLayout';
import supabase from '../../supabase/supabase';

const {
  FiInbox, FiRefreshCw, FiTrash2, FiEye, FiX, FiImage, FiExternalLink,
  FiSave, FiDownload, FiCopy, FiCheck, FiMessageCircle, FiLink
} = FiIcons;

const STATUSES = ['pending', 'confirmed', 'packing', 'ready_to_ship', 'picked_up', 'delivered', 'cancelled'];
const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  packing: 'Packing',
  ready_to_ship: 'Ready to Ship',
  picked_up: 'Picked Up',
  delivered: 'Delivered',
  cancelled: 'Cancelled'
};

function formatWhatsApp(phone) {
  const cleaned = phone.replace(/[\s\-()]/g, '');
  if (cleaned.startsWith('+')) return cleaned.slice(1);
  if (cleaned.startsWith('0')) return '94' + cleaned.slice(1);
  return cleaned;
}

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewOrder, setViewOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [orderPayment, setOrderPayment] = useState(null);
  const [shippingAgent, setShippingAgent] = useState(null);
  const [externalTracking, setExternalTracking] = useState('');
  const [savingTracking, setSavingTracking] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [publicLinkCopied, setPublicLinkCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('orders_1750000001').select('*').order('created_at', { ascending: false });
    setOrders(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    supabase.from('settings_1750000003').select('*').eq('key', 'shipping_agent').limit(1).single()
      .then(({ data }) => { if (data) setShippingAgent(data.value); });
  }, []);

  const updateStatus = async (orderId, status) => {
    const { data: payment } = await supabase
      .from('order_payments_1750000002')
      .select('status')
      .eq('order_id', orderId)
      .limit(1)
      .maybeSingle();

    if (payment?.status !== 'confirmed') {
      window.alert('Please verify the uploaded payment screenshot before changing the order status.');
      return;
    }

    if (!window.confirm(`Are you sure change status to: ${STATUS_LABELS[status]}?`)) return;

    const updateData = { status, updated_at: new Date().toISOString() };
    if (status === 'delivered') {
      updateData.delivered_at = new Date().toISOString();
    }
    await supabase.from('orders_1750000001').update(updateData).eq('id', orderId);
    setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, ...updateData } : o));
    if (viewOrder && viewOrder.id === orderId) {
      setViewOrder((prev) => ({ ...prev, ...updateData }));
    }
  };

  const confirmPayment = async () => {
    if (!viewOrder || !orderPayment) return;
    await supabase.from('order_payments_1750000002')
      .update({ status: 'confirmed', notes: 'Payment verified by admin' })
      .eq('id', orderPayment.id);
    setOrderPayment((prev) => ({ ...prev, status: 'confirmed' }));
    await updateStatus(viewOrder.id, 'confirmed');
  };

  const saveExternalTracking = async () => {
    if (!viewOrder) return;
    setSavingTracking(true);
    await supabase.from('orders_1750000001')
      .update({ external_tracking: externalTracking, updated_at: new Date().toISOString() })
      .eq('id', viewOrder.id);
    setOrders((prev) => prev.map((o) => o.id === viewOrder.id ? { ...o, external_tracking: externalTracking } : o));
    setViewOrder((prev) => ({ ...prev, external_tracking: externalTracking }));
    setSavingTracking(false);
  };

  const copyTrackingLink = () => {
    if (!viewOrder || !shippingAgent || !externalTracking) return;
    const link = `${shippingAgent.trackingUrl}${externalTracking}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
  };

  const copyPublicTrackingLink = () => {
    if (!viewOrder || !viewOrder.tracking_token) return;
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/track-public/${viewOrder.tracking_token}`;
    navigator.clipboard.writeText(link);
    setPublicLinkCopied(true);
    setTimeout(() => setPublicLinkCopied(false), 2500);
  };

  const downloadScreenshot = () => {
    if (!orderPayment?.screenshot_url) return;
    const a = document.createElement('a');
    a.href = orderPayment.screenshot_url;
    a.download = `payment-${viewOrder.id.slice(0, 8)}.png`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Delete this order?')) return;

    const { error: salesError } = await supabase
      .from('influencer_sales_1750000011')
      .delete()
      .eq('order_id', orderId);

    if (salesError) {
      window.alert(`Unable to remove the linked influencer sale: ${salesError.message}`);
      return;
    }

    const { error } = await supabase
      .from('orders_1750000001')
      .delete()
      .eq('id', orderId);

    if (error) {
      window.alert(`Unable to delete this order: ${error.message}`);
      return;
    }

    setOrders((prev) => prev.filter((o) => o.id !== orderId));
    if (viewOrder?.id === orderId) setViewOrder(null);
  };

  const viewOrderDetails = async (order) => {
    setViewOrder(order);
    setExternalTracking(order.external_tracking || '');
    setLinkCopied(false);
    setPublicLinkCopied(false);
    const { data: items } = await supabase.from('order_items_1750000001').select('*').eq('order_id', order.id);
    setOrderItems(items || []);
    const { data: payment } = await supabase.from('order_payments_1750000002').select('*').eq('order_id', order.id).limit(1).single();
    setOrderPayment(payment);
  };

  const getWhatsAppUrl = (phone) => {
    return `https://wa.me/${formatWhatsApp(phone)}`;
  };

  const filtered = orders.filter((o) =>
    o.customer_name.toLowerCase().includes(search.toLowerCase()) ||
    o.customer_email.toLowerCase().includes(search.toLowerCase()) ||
    o.id.slice(0, 8).includes(search.toLowerCase()) ||
    (o.tracking_number && o.tracking_number.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div><h1>Orders</h1><p>Manage and track all customer orders</p></div>
        <div className="admin-header-actions">
          <input className="admin-search" placeholder="Search orders…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <button className="admin-btn secondary" onClick={load}><SafeIcon icon={FiRefreshCw} /></button>
        </div>
      </div>

      <div className="admin-card">
        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="admin-empty"><SafeIcon icon={FiInbox} /><p>No orders found</p></div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>Order</th><th>Tracking</th><th>Customer</th><th>Email</th><th>Amount</th><th>Status</th><th>Date</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map((order) => (
                  <tr key={order.id}>
                    <td className="mono">{order.id.slice(0, 8)}</td>
                    <td className="mono">{order.tracking_number || '—'}</td>
                    <td>{order.customer_name}</td>
                    <td className="small-text">{order.customer_email}</td>
                    <td>Rs. {Number(order.total_amount).toLocaleString()}</td>
                    <td>
                      <select className="admin-status-select" value={order.status} onChange={(e) => updateStatus(order.id, e.target.value)}>
                        {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </select>
                    </td>
                    <td className="small-text">{new Date(order.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="admin-row-actions">
                        <a
                          href={getWhatsAppUrl(order.customer_phone)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-icon-btn whatsapp-btn"
                          title="WhatsApp customer"
                        >
                          <SafeIcon icon={FiMessageCircle} />
                        </a>
                        <button className="admin-icon-btn" onClick={() => viewOrderDetails(order)} title="View"><SafeIcon icon={FiEye} /></button>
                        <button className="admin-icon-btn danger" onClick={() => deleteOrder(order.id)} title="Delete"><SafeIcon icon={FiTrash2} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewOrder && (
        <div className="admin-modal-overlay" onClick={() => setViewOrder(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Order #{viewOrder.id.slice(0, 8)}</h2>
              <button className="admin-icon-btn" onClick={() => setViewOrder(null)}><SafeIcon icon={FiX} /></button>
            </div>
            <div className="admin-modal-body">
              <div className="order-tracking-id">
                <span>Tracking Number</span>
                <strong>{viewOrder.tracking_number || 'Not assigned'}</strong>
              </div>

              {viewOrder.tracking_token && (
                <div className="public-tracking-link-section">
                  <div className="public-link-header">
                    <SafeIcon icon={FiLink} />
                    <span>Public Tracking Link</span>
                    <span className="public-link-hint">Share with customer — expires 30 days after delivery</span>
                  </div>
                  <div className="public-link-actions">
                    <button className="copy-link-btn" onClick={copyPublicTrackingLink}>
                      <SafeIcon icon={publicLinkCopied ? FiCheck : FiCopy} />
                      {publicLinkCopied ? 'Link Copied!' : 'Copy Public Tracking Link'}
                    </button>
                  </div>
                </div>
              )}

              <div className="order-detail-grid">
                <div>
                  <h3>Customer</h3>
                  <p><strong>{viewOrder.customer_name}</strong></p>
                  <p>{viewOrder.customer_email}</p>
                  <div className="customer-phone-row">
                    <p>{viewOrder.customer_phone}</p>
                    <a
                      href={getWhatsAppUrl(viewOrder.customer_phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whatsapp-inline-btn"
                    >
                      <SafeIcon icon={FiMessageCircle} /> WhatsApp
                    </a>
                  </div>
                  <p>{viewOrder.shipping_address}</p>
                  <p>{viewOrder.shipping_district} — Rs. {Number(viewOrder.shipping_cost || 0).toLocaleString()}</p>
                </div>
                <div>
                  <h3>Items</h3>
                  {orderItems.map((item) => (
                    <div className="order-item-row" key={item.id}>
                      <span>{item.product_name} × {item.quantity}</span>
                      <strong>Rs. {(Number(item.unit_price) * item.quantity).toLocaleString()}</strong>
                    </div>
                  ))}
                  <div className="order-total-row">
                    <span>Total</span>
                    <strong>Rs. {Number(viewOrder.total_amount).toLocaleString()}</strong>
                  </div>
                </div>
              </div>

              <div className="order-status-management">
                <h3>Update Status</h3>
                <p className="tracking-hint">
                  {orderPayment?.status === 'confirmed'
                    ? 'Select a status below. You will be asked to confirm before it is updated.'
                    : 'Verify the payment screenshot below to unlock status updates.'}
                </p>
                <div className="status-buttons">
                  {STATUSES.filter((s) => s !== 'cancelled').map((s) => (
                    <button
                      key={s}
                      className={`status-action-btn ${viewOrder.status === s ? 'active' : ''}`}
                      onClick={() => updateStatus(viewOrder.id, s)}
                      disabled={orderPayment?.status !== 'confirmed'}
                      title={orderPayment?.status !== 'confirmed' ? 'Confirm payment first' : `Change status to ${STATUS_LABELS[s]}`}
                    >
                      {STATUS_LABELS[s]}
                    </button>
                  ))}
                  <button
                    className={`status-action-btn danger ${viewOrder.status === 'cancelled' ? 'active' : ''}`}
                    onClick={() => updateStatus(viewOrder.id, 'cancelled')}
                    disabled={orderPayment?.status !== 'confirmed'}
                    title={orderPayment?.status !== 'confirmed' ? 'Confirm payment first' : 'Change status to Cancelled'}
                  >
                    Cancelled
                  </button>
                </div>
              </div>

              {(viewOrder.status === 'ready_to_ship' || viewOrder.status === 'picked_up' || viewOrder.status === 'delivered') && (
                <div className="order-external-tracking">
                  <h3><SafeIcon icon={FiExternalLink} /> Package Tracking ({shippingAgent?.name || 'Shipping Agent'})</h3>
                  <p className="tracking-hint">Enter the tracking number from {shippingAgent?.name || 'the shipping agent'}</p>
                  <div className="tracking-input-row">
                    <input
                      value={externalTracking}
                      onChange={(e) => setExternalTracking(e.target.value)}
                      placeholder="Enter package tracking number"
                    />
                    <button className="admin-btn primary" onClick={saveExternalTracking} disabled={savingTracking}>
                      <SafeIcon icon={FiSave} /> {savingTracking ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  {externalTracking && shippingAgent && (
                    <div className="tracking-link-actions">
                      <a
                        href={`${shippingAgent.trackingUrl}${externalTracking}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tracking-link-preview"
                      >
                        <SafeIcon icon={FiExternalLink} /> Track on {shippingAgent.name} →
                      </a>
                      <button className="copy-link-btn" onClick={copyTrackingLink}>
                        <SafeIcon icon={linkCopied ? FiCheck : FiCopy} />
                        {linkCopied ? 'Link Copied!' : 'Copy Tracking Link'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {orderPayment && orderPayment.screenshot_url && (
                <div className="payment-screenshot">
                  <h3><SafeIcon icon={FiImage} /> Payment Screenshot</h3>
                  <div className="payment-screenshot-actions">
                    <span className={`payment-status-badge ${orderPayment.status}`}>
                      {orderPayment.status === 'confirmed' ? '✓ Verified' : orderPayment.status === 'submitted' ? 'Awaiting Verification' : 'Pending'}
                    </span>
                    <div className="payment-action-btns">
                      <button className="admin-btn secondary" onClick={downloadScreenshot}>
                        <SafeIcon icon={FiDownload} /> Download
                      </button>
                      {orderPayment.status !== 'confirmed' && (
                        <button className="admin-btn primary" onClick={confirmPayment}>
                          <SafeIcon icon={FiCheck} /> Confirm Payment
                        </button>
                      )}
                    </div>
                  </div>
                  <a href={orderPayment.screenshot_url} target="_blank" rel="noopener noreferrer">
                    <img src={orderPayment.screenshot_url} alt="Payment receipt" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminOrders;